import { Color, ResponsiveAttribute } from '../../basic';
import { GsStructBorderRadius, GsStructFont } from '../../structure';
import { ISides } from '../config';
import { IIcon } from '../icons/icons';

/** Available header templates */
export type HeaderTemplate = 'basic' | 'slim';
/**
 * Available header layouts
 * LayoutContent = Immer mit Container
 * Layout = Immer ohne Container
 */
export type HeaderLayout = 'LayoutContent' | 'Layout';
/**
 * Available header action types. `inquiry_shoppingcart` is the Anfrageliste action — the underscore
 * mirrors the backend link target `inquiry.shoppingcart` while staying a valid Angular form-control name
 * (a dot would be parsed as a nested form path).
 */
export type ConfigHeaderAction = 'watchlist' | 'shoppingcart' | 'dashboard' | 'login' | 'logout' | 'inquiry_shoppingcart';
/** Header actions that require the user to be logged in */
export const CONFIG_HEADER_ACTIONS_NEEDS_LOGIN = ['dashboard', 'logout'];
/** Header actions that require the user to be logged out */
export const CONFIG_HEADER_ACTIONS_WITHOUT_LOGIN = ['login'];
/** Header actions that require the webshop module to be active */
export const CONFIG_HEADER_ACTIONS_NEEDS_WEBSHOP = ['watchlist', 'shoppingcart', 'inquiry_shoppingcart'];
/** Header actions that require an inquiry checkout page to exist (init.hasInquiry) */
export const CONFIG_HEADER_ACTIONS_NEEDS_INQUIRY = ['inquiry_shoppingcart'];
/**
 * Maps a header action to the backend link-target name it resolves to, when the two differ. The action
 * key keeps the underscore form (usable as an Angular form-control name); the link is looked up under the
 * real VirtualSiteMap link target (with the dot). Actions absent here resolve against their own key.
 */
export const CONFIG_HEADER_ACTION_LINK_TARGETS: Partial<Record<ConfigHeaderAction, string>> = {
  inquiry_shoppingcart: 'inquiry.shoppingcart'
};
/** Available topbar templates */
export type ConfigHeaderTopbarTemplate = 'openingHours' | 'phoneNumber' | 'custom';
/** Available menubar alignments */
export type ConfigHeaderMenubarAlignment = 'left' | 'center' | 'right' | 'justify-between';

/** Config structure for the header */
export interface IConfigHeader {
  /** Should the header be sticky and stay on top of the view when scrolled? */
  sticky?: boolean;
  /** If true, a shadow will be shown below the header */
  showShadow?: boolean;
  /** The layout style for the header */
  layout?: HeaderLayout;
  /** The selected template for the header */
  template?: HeaderTemplate;
  /** The background color of the header */
  backgroundColor?: Color;
  /** Padding for the header (in px) */
  padding?: ResponsiveAttribute<ISides>;
  /** The main area of the header, including the logo, search and action-buttons */
  mainbar?: IConfigHeaderMainbar;
  /** Settings for the topbar */
  topbar?: IConfigHeaderTopbar;
  /** Settings for the menubar/navbar */
  menubar?: IConfigHeaderMenubar;
  /** Settings for the article tiles of every component rendering product items */
  productItems?: IConfigProductItemsOptions;
}

/** Config structure for the article tiles (product items) of every component rendering them */
export interface IConfigProductItemsOptions {
  /** If true, article tiles show the watchlist button (CS4-1278) */
  showWatchlistButton?: boolean;
  /** If true, article tiles show the quick-buy shopping cart button (CS4-1278) */
  showShoppingCartButton?: boolean;
}

/** Config structure for the mainbar inside the header */
export interface IConfigHeaderMainbar extends IConfigHeaderActiveOptions {
  /** Settings defining the design and functionality of the searchbar */
  search?: IConfigHeaderSearchOptions;
  /** Settings for the logo inside the header */
  logo?: IConfigHeaderLogoOptions;
  /** Settings for the myChainstore button inside the header */
  myChainstore?: IConfigHeaderMyChainstoreOptions;
  /** Settings for the language selector inside the header */
  language?: IConfigHeaderActiveOptions;
  /** Settings for the icons inside the header on the right side for actions */
  actions?: IConfigHeaderActionsOptions;
}

/** Config structure for the topbar inside the header */
export interface IConfigHeaderTopbar extends IConfigHeaderActiveOptions {
  /** Should the topbar stay visible while scrolling? */
  sticky?: boolean;
  /** The selected template for the topbar */
  template?: ConfigHeaderTopbarTemplate;
  /** Whether a divider line is shown at the bottom of the topbar, separating it from the header */
  divider?: boolean;
  /** Background color for the topbar */
  backgroundColor?: Color;
  /** Text color for the topbar */
  textColor?: Color;
  /** Font settings for the topbar */
  font?: GsStructFont;
  /** Text size for the topbar content (in px) */
  textSize?: number;
}

/** Container item structure for topbar */
export interface ITopbarContainerItem {
  /** Container item ID */
  containerItemID?: number;
  /** Title to display */
  title?: string;
  /** Index/order */
  index?: number;
  /** Link URL (Details field) */
  details?: string;
  /** Is inactive? */
  inactive?: boolean;
}

/** Config structure for the menubar inside the header */
export interface IConfigHeaderMenubar {
  /** Background color of the menubar */
  backgroundColor?: Color;
  /** Text color of the menu-items */
  textColor?: Color;
  /** Font settings for the menu-items */
  font?: GsStructFont;
  /** Text size for the menu-items (in px) */
  textSize?: number;
  /** Background color of the active menu-item */
  activeBackgroundColor?: Color;
  /** Color of the active menu-item */
  activeTextColor?: Color;
  /** Whether the active menu-item should be underlined */
  activeUnderlined?: boolean;
  /** Vertical padding of the menubar (in px) */
  verticalPadding?: number;
  /** Gap between the menu-items (in px) */
  gapBetweenItems?: number;
  /** Alignment of the menu-items eg.: "center", "right", "left" or "justify-between" */
  alignment?: ConfigHeaderMenubarAlignment;
  /** Whether today's opening hours are shown in the mobile sidebar (burger) menu */
  showOpeningHoursInSidebar?: boolean;
  /** Settings for the dropdownMenu */
  dropdownMenu?: IConfigHeaderDropdownMenuOptions;
  /** Settings for the hamburger-button on mobile */
  burgerButton?: IConfigHeaderBurgerButtonOptions;
}

/** Where the myChainstore selection is offered */
export type ConfigHeaderMyChainstoreDisplayMode = 'header' | 'sidemenu' | 'both';

/** Config structure for the myChainstore selection inside the header */
export interface IConfigHeaderMyChainstoreOptions extends IConfigHeaderBaseOptions {
  /** Where the selection is offered — as an icon in the mainbar, as an entry in the sidebar menu, or both */
  displayMode?: ConfigHeaderMyChainstoreDisplayMode;
}

/** Config structure for the search inside the header */
export interface IConfigHeaderSearchOptions extends IConfigHeaderBaseOptions {
  /** Show the search inside a modal? If false will show in fullscreen */
  showAsModal?: boolean;
  /** Border radius for the typeahead result images */
  borderRadius?: GsStructBorderRadius;
}

/** Config structure for the logo inside the header */
export interface IConfigHeaderLogoOptions extends IConfigHeaderActiveOptions {
  /** If true, click will redirect to the home link-target instead of the linkUrl */
  useHomeLinkTarget?: boolean;
  /** If set (and useHomeLinkTarget=false), where should the logo redirect to when clicked */
  linkUrl?: string;
  /** Should the logo use the full height of the header area? (mobile: just more height) */
  useFullHeight?: boolean;
}

/** Config structure for the actions inside the header */
export interface IConfigHeaderActionsOptions {
  /** Color of the action icons */
  iconColor?: Color;
  /** Hover color of the action icons */
  iconColorHover?: Color;
  /** Gap between the action icons (in px) */
  gap?: number;
  /** If true, the configured action icons are shown in the mobile sidebar menu */
  showIconInSidebar?: boolean;
  /** The configured actions inside the header */
  actions?: Partial<Record<ConfigHeaderAction, IConfigActionOptions>>;
}

export interface IConfigHeaderDropdownMenuOptions {
  /** Font settings for the menu-items */
  font?: GsStructFont;
  /** Text size for the menu-items (in px) */
  textSize?: number;
  /** Text color of the menu-items */
  textColor?: Color;
  /** Text color of the active dropdown menu-item */
  activeTextColor?: Color;
  /** Background color of the active dropdown menu-item */
  activeBackgroundColor?: Color;
  /** Whether the active dropdown menu-item should be underlined */
  activeUnderlined?: boolean;
}

export interface IConfigHeaderBurgerButtonOptions extends IConfigHeaderActiveOptions {
  /** Should the burger-button also kept active on desktop? */
  keepActiveOnDesktop?: boolean;
  /** The icon settings for the burger-button */
  icon?: IIcon;
  /** Color of the burger-button */
  color?: Color;
}

/** Config structure for action options for the header-actions */
export interface IConfigActionOptions {
  /** Sort order index for display (highest number = first) */
  orderIndex?: number;
  /** Responsive settings for the action visibility */
  active?: ResponsiveAttribute<boolean>;
  /** The icon settings for the action */
  icon?: IIcon;
}

/** Config structure for active options */
export interface IConfigHeaderActiveOptions {
  /** Should the element be active and visible */
  active?: boolean;
}

/** Config structure for base options using an icon */
export interface IConfigHeaderBaseOptions extends IConfigHeaderActiveOptions {
  /** The icon settings for the element */
  icon?: IIcon;
}
